"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Check } from "lucide-react"
import { ChevronDownIcon } from "@/components/brand-kit/icon-sprite"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import type { ConcreteCatalogType, TypeBucket } from "./types"

interface TypeFilterPillsProps {
  buckets: TypeBucket[]
  /** Currently active multi-select — empty array = "All". */
  activeTypes: ConcreteCatalogType[]
  /**
   * Catalog base path, resolved on the server. Previously derived from
   * `usePathname()` alongside a `useSearchParams()` read, which bailed the
   * page-wide Suspense boundary out of the static prerender and left the
   * canonical URL serving a spinner to non-JS crawlers (FRIDGE-237).
   */
  basePath: string
}

/**
 * Multi-select type filter for the brand catalog.
 *
 * Both presentations let the user pick any combination of types. The URL
 * is the source of truth: `?type=french-door,side-by-side` for two
 * selected, no `type` param for "All". Toggling a type rewrites the
 * comma-separated list and clears `?page` so pagination doesn't carry
 * stale offsets across filter changes.
 *
 *   - **Mobile (< sm)** — `Popover` + custom rows with a left-side
 *     checkbox square. No right-side indicator. The trigger summarises
 *     the selection ("All", "French Door", "3 product types"). A
 *     "Clear" link in the panel header empties the selection.
 *
 *   - **sm and up** — pill bar where each pill is an independent toggle.
 *     Clicking a pill adds or removes that type. The "All" pill is
 *     active when the selection set is empty and clicking it clears
 *     all other selections.
 */
export function TypeFilterPills({ buckets, activeTypes, basePath }: TypeFilterPillsProps) {
  const router = useRouter()

  const writeSelection = React.useCallback(
    (next: ConcreteCatalogType[]) => {
      // `page` is deliberately never carried over — changing the filter resets
      // to page 1, so the only param to rebuild is `type`.
      const params = new URLSearchParams()
      if (next.length > 0) params.set("type", next.join(","))
      const qs = params.toString()
      router.push(qs ? `${basePath}?${qs}` : basePath, { scroll: false })
    },
    [basePath, router],
  )

  const toggleType = React.useCallback(
    (key: ConcreteCatalogType) => {
      const set = new Set<ConcreteCatalogType>(activeTypes)
      if (set.has(key)) set.delete(key)
      else set.add(key)
      writeSelection(Array.from(set))
    },
    [activeTypes, writeSelection],
  )

  const clearAll = React.useCallback(() => writeSelection([]), [writeSelection])

  // Concrete buckets only — the "All" sentinel is rendered as its own
  // affordance (clear pill on desktop, label in the popover header on
  // mobile) since multi-select doesn't have a single "all" state to
  // toggle the way the pill bar used to.
  const concreteBuckets = React.useMemo(
    () => buckets.filter(b => b.key !== "all"),
    [buckets],
  )
  const allBucket = buckets.find(b => b.key === "all")
  const activeSet = React.useMemo(() => new Set(activeTypes), [activeTypes])

  // Trigger label — summary of the current selection
  const triggerLabel = React.useMemo(() => {
    if (activeTypes.length === 0) return "All product types"
    if (activeTypes.length === 1) {
      const b = concreteBuckets.find(x => x.key === activeTypes[0])
      return b ? b.label : "1 type"
    }
    return `${activeTypes.length} types selected`
  }, [activeTypes, concreteBuckets])

  const triggerCount = activeTypes.length === 0
    ? (allBucket?.count ?? 0)
    : activeTypes
        .map(t => concreteBuckets.find(b => b.key === t)?.count ?? 0)
        .reduce((a, b) => a + b, 0)

  return (
    <>
      {/* Mobile: brand-kit Popover with checkbox rows. */}
      <div className="sm:hidden">
        <span
          id="brand-type-filter-label"
          className="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500"
        >
          Filter by product type
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-labelledby="brand-type-filter-label"
              className="flex h-11 w-full items-center justify-between rounded-full border border-gray-200 bg-white px-4 text-[14px] font-semibold text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.04)] focus-visible:border-[#ff9951] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#ff9951]/15"
            >
              <span className="truncate">
                {triggerLabel}{" "}
                <span className="ml-0.5 font-semibold text-gray-500">
                  ({triggerCount.toLocaleString()})
                </span>
              </span>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            sideOffset={6}
            align="start"
            className="w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.12)]"
          >
            <div className="mb-1 flex items-center justify-between px-2 py-1">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500">
                Product type
              </span>
              {activeTypes.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[12px] font-semibold text-[#0469f0] hover:text-[#003ad0]"
                >
                  Clear ({activeTypes.length})
                </button>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              {concreteBuckets.map(b => {
                const active = activeSet.has(b.key as ConcreteCatalogType)
                return (
                  <button
                    key={b.key}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={active}
                    onClick={() => toggleType(b.key as ConcreteCatalogType)}
                    className={`flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-semibold transition-colors focus:outline-none focus-visible:bg-gray-50 ${
                      active ? "bg-[#ff9951]/10 text-[#c2410c]" : "text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-[5px] border transition-colors ${
                        active
                          ? "border-[#ff9951] bg-[#ff9951] text-[var(--color-brand-action-text)]"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate">{b.label}</span>
                    <span
                      className={`text-[12px] font-semibold ${active ? "text-[#c2410c]/70" : "text-gray-500"}`}
                    >
                      {b.count.toLocaleString()}
                    </span>
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* sm and up: toggle pill bar. */}
      <div className="hidden justify-center sm:flex">
        <div
          className="inline-flex max-w-full items-center gap-1.5 overflow-x-auto rounded-full bg-gray-100 p-1.5 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
          role="group"
          aria-label="Filter by product type"
        >
          {/* "All" pill is active when the selection set is empty; clicking
              it clears any current selection back to no filter. */}
          {allBucket && (
            <button
              type="button"
              aria-pressed={activeTypes.length === 0}
              onClick={clearAll}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-150 ${
                activeTypes.length === 0
                  ? "bg-[#ff9951] text-[var(--color-brand-action-text)] shadow-[0_2px_6px_rgba(255,153,81,0.35)]"
                  : "text-gray-700 hover:bg-white hover:text-gray-900"
              }`}
            >
              {allBucket.label}{" "}
              <span
                className={`ml-1 text-[12px] font-semibold ${activeTypes.length === 0 ? "text-white/85" : "text-gray-500/70"}`}
              >
                ({allBucket.count.toLocaleString()})
              </span>
            </button>
          )}
          {concreteBuckets.map(b => {
            const active = activeSet.has(b.key as ConcreteCatalogType)
            return (
              <button
                key={b.key}
                type="button"
                aria-pressed={active}
                onClick={() => toggleType(b.key as ConcreteCatalogType)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-150 ${
                  active
                    ? "bg-[#ff9951] text-[var(--color-brand-action-text)] shadow-[0_2px_6px_rgba(255,153,81,0.35)]"
                    : "text-gray-700 hover:bg-white hover:text-gray-900"
                }`}
              >
                {b.label}{" "}
                <span
                  className={`ml-1 text-[12px] font-semibold ${active ? "text-white/85" : "text-gray-500/70"}`}
                >
                  ({b.count.toLocaleString()})
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}
